import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import CreatorStudioLayout from "./CreatorStudioLayout";
import { fetchCreatorDrops, fetchReferralsForCreator, fetchSupporters } from "../../services/creatorStudioService";
import { formatDate, timeAgo } from "./creatorHelpers";

export default function Subscribers() {
  const { currentUser, userProfile } = useAuth();
  const [supporters, setSupporters] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!currentUser?.uid) return;
    setLoading(true);
    Promise.all([
      fetchSupporters(currentUser.uid),
      fetchReferralsForCreator(currentUser.uid),
      fetchCreatorDrops(currentUser.uid),
    ])
      .then(([sup, ref, dr]) => {
        setSupporters(sup);
        setReferrals(ref);
        setDrops(dr);
      })
      .finally(() => setLoading(false));
  }, [currentUser?.uid]);

  const referredUserIds = useMemo(
    () => new Set(referrals.map((r) => r.referredUserId || r.userId).filter(Boolean)),
    [referrals]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return supporters;
    const q = search.toLowerCase();
    return supporters.filter((s) => {
      const name = s.fan?.displayName || s.fan?.username || "";
      const handle = s.fan?.handle || s.fan?.username || "";
      return name.toLowerCase().includes(q) || handle.toLowerCase().includes(q);
    });
  }, [supporters, search]);

  const topFans = useMemo(() => {
    return [...supporters]
      .sort((a, b) => (b.joinedAt?.seconds || 0) - (a.joinedAt?.seconds || 0))
      .slice(0, 3);
  }, [supporters]);

  const referredCount = supporters.filter((s) => referredUserIds.has(s.userId)).length;

  if (loading) {
    return (
      <CreatorStudioLayout>
        <div className="cs-loading">Loading subscribers…</div>
      </CreatorStudioLayout>
    );
  }

  return (
    <CreatorStudioLayout>
      <div className="cs-page-eyebrow">Creator Studio</div>
      <h1 className="cs-page-title" style={{ marginBottom: 32 }}>Your <em>Subscribers</em></h1>

      <div className="cs-stats-row">
        <div className="cs-stat-card">
          <div className="cs-stat-num">{supporters.length}</div>
          <div className="cs-stat-label" style={{ marginBottom: 0, marginTop: 4 }}>Total subscribers</div>
          <div className="cs-stat-change up" style={{ marginTop: 8 }}>tier supporters</div>
        </div>
        <div className="cs-stat-card">
          <div className="cs-stat-num gold">{referredCount}</div>
          <div className="cs-stat-label" style={{ marginBottom: 0, marginTop: 4 }}>Referred by you</div>
        </div>
        <div className="cs-stat-card">
          <div className="cs-stat-num">{drops.length ? Math.round((supporters.length / Math.max(drops.length, 1)) * 10) / 10 : 0}</div>
          <div className="cs-stat-label" style={{ marginBottom: 0, marginTop: 4 }}>Avg drops unlocked</div>
          <div className="cs-stat-hand">per subscriber</div>
        </div>
        <div className="cs-stat-card">
          <div className="cs-stat-num">{userProfile?.followerCount || 0}</div>
          <div className="cs-stat-label" style={{ marginBottom: 0, marginTop: 4 }}>Followers</div>
          <div className="cs-stat-hand">all fans</div>
        </div>
      </div>

      <div className="cs-card-head" style={{ marginBottom: 16, paddingBottom: 0, border: "none" }}>
        <div>
          <div className="cs-card-title">Your top fans this month</div>
          <div className="cs-card-sub" style={{ fontFamily: "Caveat, cursive", fontSize: 14, color: "var(--gold)" }}>
            private — only you can see this
          </div>
        </div>
      </div>

      <div className="cs-fan-grid">
        {topFans.length === 0 ? (
          <div className="cs-empty" style={{ gridColumn: "1 / -1" }}>No subscribers yet.</div>
        ) : (
          topFans.map((fan, i) => {
            const f = fan.fan || {};
            const name = f.displayName || f.username || "Fan";
            const avatar = f.avatar || f.avatarUrl || `https://i.pravatar.cc/80?u=${fan.userId}`;
            return (
              <div key={fan.id} className={`cs-fan-card${i === 0 ? " top" : ""}`}>
                <div className={`cs-fan-rank${i === 0 ? " gold" : ""}`}>{i === 0 ? "✦ #1 Fan" : `#${i + 1} Fan`}</div>
                <img className={`cs-fan-av${i === 0 ? " gold-border" : ""}`} src={avatar} alt="" />
                <div className="cs-drop-title">{name}</div>
                <div className="cs-drop-meta" style={{ lineHeight: 1.6 }}>
                  {fan.tierName || "Subscriber"}<br />
                  {referredUserIds.has(fan.userId) ? "Referred fan" : "Gallery discovery"}
                </div>
                <div className="cs-earn-sub">Since {formatDate(fan.joinedAt)}</div>
              </div>
            );
          })
        )}
      </div>

      <div className="cs-card-head" style={{ marginBottom: 16, paddingBottom: 0, border: "none" }}>
        <div className="cs-card-title">All subscribers</div>
        <div className="cs-filter-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9A9088" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          <input type="text" placeholder="Search subscribers…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="cs-sub-table">
        <div className="cs-sub-header">
          <span>Fan</span>
          <span>Tier</span>
          <span>Subscriber since</span>
          <span>Source</span>
        </div>
        {filtered.length === 0 ? (
          <div className="cs-empty">No subscribers found.</div>
        ) : (
          filtered.map((sub) => {
            const f = sub.fan || {};
            const name = f.displayName || f.username || "Fan";
            const handle = f.handle || f.username || "fan";
            const avatar = f.avatar || f.avatarUrl || `https://i.pravatar.cc/80?u=${sub.userId}`;
            const isReferred = referredUserIds.has(sub.userId);
            return (
              <div key={sub.id} className="cs-sub-row">
                <div className="cs-sub-info">
                  <img className="cs-sub-av" src={avatar} alt="" />
                  <div>
                    <div className="cs-drop-title" style={{ fontSize: 13 }}>{name}</div>
                    <div className="cs-drop-meta">@{handle}</div>
                  </div>
                </div>
                <div className="cs-earn-amount" style={{ fontSize: 13 }}>{sub.tierName || "Supporter"}</div>
                <div className="cs-earn-sub">{timeAgo(sub.joinedAt)}</div>
                <div>
                  {isReferred ? (
                    <span className="cs-referred-badge">✓ Referred</span>
                  ) : (
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Gallery</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </CreatorStudioLayout>
  );
}
